import { useEffect, useState } from 'react';

import Container from '@ui/container/container';
import { getSystemTimezone } from '@ui/calendar/utils';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import EditStateWrapper from '@ui/edit-state-wrapper/edit-state-wrapper';
import UnfocusedSessionDateContent from './unfocused-session-date-content';
import FocusedSessionDateContent from './focused-session-date-content';

import styles from './session-stream-info.module.css';
import { useAppSelector } from '@/store/hooks';
import { selectWorkingSession } from '@/store/slices/project/selectors';

type TSessionDateInfoProps = {
  /** if syncProjectDates true, will update the project dates when the session dates are updated */
  syncProjectDates?: boolean;
  selectedTimezone?: string;
};

const SessionDateInfo = ({ syncProjectDates, selectedTimezone: selectedTimezoneProp }: TSessionDateInfoProps) => {
  const session = useAppSelector(selectWorkingSession);

  const [selectedDates, setSelectedDates] = useState<{
    startDate: number | null;
    endDate: number | null;
    selectedTimezone: string;
  }>({
    startDate: null,
    endDate: null,
    selectedTimezone: selectedTimezoneProp || getSystemTimezone(),
  });

  useEffect(() => {
    if (selectedTimezoneProp) {
      setSelectedDates((prev) => ({ ...prev, selectedTimezone: selectedTimezoneProp }));
    }
  }, [selectedTimezoneProp]);

  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: GENERAL_EDITABLE_PERMISSIONS,
  });

  // make sure we have a session first, or we'll falsely trigger keepOpen
  const requiresTimeChange = !!(
    session &&
    (!session?.timestamp || Object.values(session?.requires_time_change || {}).some(Boolean))
  );

  return (
    <Container data-component="SessionDateInfo">
      <EditStateWrapper
        disabled={!hasPermissions}
        Label="DATE & TIME"
        headerClassName="position-absolute width-100-percent"
        className={`${styles['session-stream-info']} position-relative`}
        keepOpen={requiresTimeChange}
        applyTabSentinels
        startingSentinelAriaLabel="Starting date and time section"
        endingSentinelAriaLabel="Ending date and time section"
      >
        {({ isFocusedWithin }) => (
          <Container
            width="100%"
            height="100%"
            data-show-edit-label={isFocusedWithin ? 'false' : 'true'}
            data-has-date={!!session?.timestamp}
            transitionDuration="0ms"
            className={'margin-t-24'}
          >
            <Container
              width="100%"
              className={`flex jc-flex-end ai-center gap-12 ${!isFocusedWithin ? 'cursor-pointer' : ''} ${styles['content-container']}`}
              borderRadius="8px"
              transitionDuration={isFocusedWithin ? '0ms' : undefined}
            >
              <UnfocusedSessionDateContent
                selectedDates={selectedDates}
                session={session}
                isFocusedWithin={isFocusedWithin}
              />
              <FocusedSessionDateContent
                session={session}
                syncProjectDates={syncProjectDates}
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                isFocusedWithin={isFocusedWithin}
              />
            </Container>
          </Container>
        )}
      </EditStateWrapper>
    </Container>
  );
};

export default SessionDateInfo;
